import { SessionStorage } from '@mentimeter/storage';
import type { SocialAuthProvider } from '../types';
import { generateRandomHexString } from '../utils/generateRandomHexString';
import { SOCIAL_AUTH_STORAGE_KEYS } from './storageKeys';

interface SocialAuthProviderConfig {
  authBaseURL: string;
  clientID: string;
}

const SOCIAL_AUTH_PROVIDERS: Record<
  SocialAuthProvider,
  SocialAuthProviderConfig
> = {
  google: {
    authBaseURL: `https://accounts.google.com/o/oauth2/auth`,
    clientID: process.env.NEXT_PUBLIC_OAUTH_GOOGLE_CLIENT_ID!,
  },
  facebook: {
    authBaseURL: `https://www.facebook.com/v11.0/dialog/oauth`,
    clientID: process.env.NEXT_PUBLIC_OAUTH_FACEBOOK_CLIENT_ID!,
  },
  windows: {
    authBaseURL: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize`,
    clientID: process.env.NEXT_PUBLIC_OAUTH_MICROSOFT_CLIENT_ID!,
  },
};

export function startSocialAuth({
  network,
  referral,
  continueUrl,
}: {
  network: SocialAuthProvider;
  referral: string;
  continueUrl: string;
}) {
  const socialAuthProvider = SOCIAL_AUTH_PROVIDERS[network];
  if (!socialAuthProvider) {
    throw new Error('Invalid network');
  }

  const oauthState = generateRandomHexString(16);
  const host = window.location.host;
  const redirectURI = `https://${host}/auth/social/${network}/callback`;

  const queryParams = new URLSearchParams({
    client_id: socialAuthProvider.clientID,
    redirect_uri: redirectURI,
    response_type: 'code id_token',
    scope: 'openid email',
    prompt: 'select_account',
    state: oauthState,
    nonce: generateRandomHexString(16),
  });

  SessionStorage.setItem({
    type: 'necessary',
    key: SOCIAL_AUTH_STORAGE_KEYS.OAUTH_STATE,
    value: oauthState,
  });
  SessionStorage.setItem({
    type: 'functional',
    key: SOCIAL_AUTH_STORAGE_KEYS.REFERRAL,
    value: referral,
  });
  SessionStorage.setItem({
    type: 'functional',
    key: SOCIAL_AUTH_STORAGE_KEYS.CONTINUE_URL,
    value: continueUrl,
  });

  const authURL = new URL(
    `${socialAuthProvider.authBaseURL}?${queryParams.toString()}`,
  );
  return authURL.toString();
}
